/*
 * The name of a DE table, from its size.
 *
 * A MODULE OF ITS OWN so the service worker can import it: activeState.ts
 * reaches api.ts at runtime, and the worker must not (sw.ts's header). The
 * Active screen, the feed and the push all name a table through this one rule
 * (decision 951), so the three cannot come to call one table two things.
 */

/** A catalogue key and the count it interpolates. */
export interface DeTableLabel {
  key: string;
  count: number;
}

/**
 * The DE table a bout is in, as a word rather than a number.
 *
 * FTL's own vocabulary and the sport's: a table of 4 is the semi-finals and a
 * table of 2 is the final, and printing "Table of 2" beside a fencer about to
 * fence for gold would be arithmetic where a name belongs. Everything wider
 * keeps the number, because "Table of 32" is what the bracket says and what a
 * coach says.
 *
 * Returns a key and the count it interpolates, the same shape outcomeLabel
 * uses, so the component does the translating and this file stays free of
 * copy.
 */
export function deTableLabel(size: number): DeTableLabel | null {
  if (!Number.isFinite(size) || size < 2) return null;
  if (size === 2) return { key: "active.de.final", count: size };
  if (size === 4) return { key: "active.de.semiFinals", count: size };
  return { key: "active.de.tableOf", count: size };
}

/** A chosen sentence, feed or push: its values and the table it names. */
interface Named {
  values: Record<string, string | number>;
  table?: DeTableLabel;
}

/**
 * The sentence with its table label, where the payload sizes the table its
 * `values.table` quotes. FTL prints headings in English whatever the venue,
 * so the label is what lets the reader's language name the table; a row
 * written before the size existed keeps the heading.
 */
export function withTableLabel<S extends Named>(s: S, p: { table_size?: unknown }): S {
  if (!s.values.table) return s;
  const label = typeof p.table_size === "number" ? deTableLabel(p.table_size) : null;
  return label ? { ...s, table: label } : s;
}

/**
 * `values` with the table named in the reader's language, for the two
 * renderers: the feed's `t()` and the service worker's catalogue lookup.
 */
export function withTableName(
  s: Named,
  name: (label: DeTableLabel) => string | null,
): Record<string, string | number> {
  if (!s.table) return s.values;
  const named = name(s.table);
  return named ? { ...s.values, table: named } : s.values;
}
